"use client";

import Link from "next/link";
import { useCallback, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import FaqAnswerView from "@/components/faq/FaqAnswer";
import { selectFaqEntriesBySurface } from "@/lib/faq";
import { HOMEPAGE_COPY } from "@/lib/homepage/copy";

export default function HomepageFaqSection() {
  const { heading, viewAllLabel, viewAllHref } = HOMEPAGE_COPY.faq;
  const items = selectFaqEntriesBySurface("homepage");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = useCallback((index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  }, []);

  return (
    <section className="mb-16 w-full max-w-7xl" id="faqs">
      <h2 className="mb-8 text-center text-3xl font-semibold md:text-4xl">
        {heading}
      </h2>
      <div className="mx-auto max-w-3xl space-y-4">
        {items.map((faq, index) => {
          const isOpen = openFaqIndex === index;
          return (
            <div
              key={faq.id}
              className="overflow-hidden rounded-lg border border-border"
            >
              <button
                type="button"
                onClick={() => toggleFaq(index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between bg-surface p-4 text-left transition hover:bg-secondary"
              >
                <span className="pr-3 font-medium">{faq.question}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 shrink-0 text-text-light transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden px-4 transition-all duration-300 ease-in-out ${
                  isOpen ? "max-h-[40rem] py-4" : "max-h-0"
                }`}
              >
                <div className="text-text-light">
                  <FaqAnswerView answer={faq.answer} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-center">
        <Link
          href={viewAllHref}
          className="font-medium text-primary hover:underline"
        >
          {viewAllLabel}
        </Link>
      </div>
    </section>
  );
}
